// This component is used to edit and create portals.
// Still to do:
// - Handle network errors (unauthorized, etc.)
import { useState } from 'react';

import { type InternalRefetchQueriesInclude } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { HotelIcon, ListIcon, Trash2Icon } from 'lucide-react';
import {
  Navigate,
  Route,
  Routes,
  matchPath,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { Portals_Types_Enum_Enum } from '@graphql/__generated__/graphql';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useNavigateWithSearchParams } from '@modules/_shared/hooks/useNavigateWithSearchParams';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { PortalIdxContentDialog } from '@modules/portals/PortalIdxContent';
import { PortalImport } from '@modules/portals/PortalImport';
import { PortalSettings } from '@modules/portals/PortalSettings';
import { DELETE_PORTAL, GET_PORTAL } from '@modules/portals/portalsQueries';
import { PortalTeams } from '@modules/portals/PortalTeams';
import { useLocale } from '@shared/hooks/locale';

type PortalDrawerProps = {
  refetchQueries?: InternalRefetchQueriesInclude;
  onUpsert?: (portalId: string) => void;
};

export const PortalDrawer: React.FC<PortalDrawerProps> = ({
  refetchQueries,
  onUpsert,
}) => {
  const { t } = useLocale();
  const navigate = useNavigateWithSearchParams();
  const [searchParams] = useSearchParams();
  const { portalId } = useParams();
  const [idxFullScreen, setIdxFullScreen] = useState(false);
  const { pathname } = useLocation();
  const { me } = useAppData();
  const [idxDialogOpen, setIdxDialogOpen] = useState(false);

  const currentTab =
    matchPath('/settings/portals/:portalId/:tab', pathname)?.params.tab ??
    matchPath('/teams/:teamId/portals/:portalId/:tab', pathname)?.params.tab ??
    'settings';

  const { data } = useQuery(GET_PORTAL, {
    variables: { id: portalId ?? '' },
    skip: portalId == null,
  });

  const isEditing = !!data?.portals_by_pk?.id;

  const [deletePortal] = useMutation(DELETE_PORTAL);

  const handleClose = () => navigate('../');

  const isIdxPortal = data?.portals_by_pk?.type === Portals_Types_Enum_Enum.Idx;

  const portalType = data?.portals_by_pk?.type;
  const isImportablePortal =
    portalType === Portals_Types_Enum_Enum.Idealista ||
    portalType === Portals_Types_Enum_Enum.Pisos;
  const canImport =
    me?.is_admin === true && portalId != null && isImportablePortal;

  const tabs = [
    {
      label: t('Settings'),
      value: 'settings',
      to: {
        pathname: 'settings',
        search: searchParams.toString(),
      },
    },
    {
      label: t('Sharing'),
      value: 'sharing',
      disabled: portalId == null,
      to: {
        pathname: 'sharing',
        search: searchParams.toString(),
      },
    },
    ...(canImport
      ? [
          {
            label: t('Import'),
            value: 'import',
            to: {
              pathname: 'import',
              search: searchParams.toString(),
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <Drawer
        onClose={handleClose}
        slotProps={{
          paper: { sx: { width: ['100%', '80%'], maxWidth: 700 } },
        }}
        title={
          isEditing
            ? [t('Portals'), data.portals_by_pk?.name].join(' / ')
            : t('Create Portal')
        }
        actions={
          portalId != null
            ? [
                me?.is_admin
                  ? {
                      label: t('Delete'),
                      icon: <Trash2Icon size={20} />,
                      disabled:
                        (data?.portals_by_pk?.portal_listings_aggregate
                          ?.aggregate?.count ?? 0) > 0,
                      onClick: () => {
                        if (
                          window.confirm(
                            t('Are you sure you want to delete this portal?'),
                          )
                        ) {
                          deletePortal({
                            variables: { id: portalId },
                            update: cache => {
                              cache.evict({ id: `portals:${portalId}` });
                            },
                          }).then(() => {
                            handleClose();
                          });
                        }
                      },
                    }
                  : null,
                {
                  label: t('View listings'),
                  icon: <HotelIcon size={20} />,
                  to: {
                    pathname: `/listings`,
                    search: new URLSearchParams({
                      where: JSON.stringify({
                        portal_listings: {
                          portal_id: { _eq: portalId },
                          live: { _eq: true },
                        },
                      }),
                    }).toString(),
                  },
                },
                isIdxPortal && me?.is_admin
                  ? {
                      label: t('IDX Content'),
                      icon: <ListIcon size={20} />,
                      onClick: () => setIdxDialogOpen(true),
                    }
                  : null,
              ].filter(v => v != null)
            : undefined
        }
        currentTab={currentTab}
        tabs={tabs}
      >
        <Routes>
          <Route
            index
            element={
              <Navigate
                replace
                to={{
                  pathname: './settings',
                  search: searchParams.toString(),
                }}
              />
            }
          />
          <Route
            path="settings"
            element={
              <PortalSettings
                onClose={handleClose}
                refetchQueries={refetchQueries}
                onUpsert={onUpsert}
              />
            }
          />
          <Route path="sharing" element={<PortalTeams />} />
          {canImport && (
            // Keyed by portalId so switching portals in the drawer remounts the
            // import view: its broker pick, results, and any in-flight run must
            // not carry over into another portal's route.
            <Route path="import" element={<PortalImport key={portalId} />} />
          )}
        </Routes>
      </Drawer>

      {isIdxPortal && portalId != null && (
        <SlideDialog
          open={idxDialogOpen}
          onClose={() => {
            setIdxDialogOpen(false);
            setIdxFullScreen(false);
          }}
          title={t('IDX Content')}
          maxWidth="lg"
          dialogProps={{
            fullScreen: idxFullScreen,
          }}
          sx={{ '& .MuiDialogTitle-root': { padding: 0 } }}
          cancelText={t('Close')}
        >
          <PortalIdxContentDialog
            portalId={portalId}
            fullScreen={idxFullScreen}
            onToggleFullScreen={() => setIdxFullScreen(prev => !prev)}
          />
        </SlideDialog>
      )}
    </>
  );
};
